import React, { useState, useRef, useEffect, useMemo } from "react";
import { ensureFontLoaded } from "../../utils/fontLoader";

export interface FontOption {
  value: string;
  label: string;
  category?: "system" | "google" | "typeface";
}

interface FontGroup {
  label: string;
  fonts: FontOption[];
}

interface FontSelectorProps {
  label?: string;
  options?: FontOption[];
  value?: string;
  onChange?: (e: { target: { value: string } }) => void;
  placeholder?: string;
  previewText?: string; // Preview text to show font styling (default: "AaBbCc123")
  showGroups?: boolean;
  outputFormat?: "name" | "css"; // "name" returns just font name, "css" returns full CSS format
}

// System fonts
const SYSTEM_FONTS: FontOption[] = [
  { value: "Arial", label: "Arial", category: "system" },
  { value: "Helvetica", label: "Helvetica", category: "system" },
  { value: "Times New Roman", label: "Times New Roman", category: "system" },
  { value: "Courier New", label: "Courier New", category: "system" },
  { value: "Verdana", label: "Verdana", category: "system" },
  { value: "Georgia", label: "Georgia", category: "system" },
  { value: "Palatino", label: "Palatino", category: "system" },
  { value: "Garamond", label: "Garamond", category: "system" },
  { value: "Comic Sans MS", label: "Comic Sans MS", category: "system" },
  { value: "Trebuchet MS", label: "Trebuchet MS", category: "system" },
  { value: "Impact", label: "Impact", category: "system" },
  { value: "Lucida Console", label: "Lucida Console", category: "system" },
  { value: "Tahoma", label: "Tahoma", category: "system" },
];

// Typeface fonts from /public/typeface-fonts/ directory
const TYPEFACE_FONTS: FontOption[] = [
  { value: "Almarai", label: "Almarai", category: "typeface" },
  { value: "Anton", label: "Anton", category: "typeface" },
  { value: "Barlow Condensed", label: "Barlow Condensed", category: "typeface" },
  { value: "BBH Bartle", label: "BBH Bartle", category: "typeface" },
  { value: "Cairo", label: "Cairo", category: "typeface" },
  { value: "Caveat", label: "Caveat", category: "typeface" },
  { value: "Dancing Script", label: "Dancing Script", category: "typeface" },
  { value: "Edu SA Beginner", label: "Edu SA Beginner", category: "typeface" },
  { value: "Follow", label: "Follow", category: "typeface" },
  { value: "Great Vibes", label: "Great Vibes", category: "typeface" },
  { value: "Josefin Sans Thin", label: "Josefin Sans Thin", category: "typeface" },
  { value: "Lobster", label: "Lobster", category: "typeface" },
  { value: "Lobster Two", label: "Lobster Two", category: "typeface" },
  { value: "Noto Sans Symbols", label: "Noto Sans Symbols", category: "typeface" },
  { value: "Orbitron", label: "Orbitron", category: "typeface" },
  { value: "Pacifico", label: "Pacifico", category: "typeface" },
  { value: "Satisfy", label: "Satisfy", category: "typeface" },
  { value: "Shadows Into Light", label: "Shadows Into Light", category: "typeface" },
  { value: "Smooch Sans Thin", label: "Smooch Sans Thin", category: "typeface" },
  { value: "Space Grotesk Light", label: "Space Grotesk Light", category: "typeface" },
  { value: "Tajawal", label: "Tajawal", category: "typeface" },
];

// Arabic fonts list for categorization
const ARABIC_FONTS = [
  "Cairo", "Tajawal", "Amiri", "Almarai", "Changa", "El Messiri", 
  "Harmattan", "Lateef", "Markazi Text", "Reem Kufi", "Scheherazade New", 
  "Noto Sans Arabic", "Noto Naskh Arabic", "Noto Kufi Arabic", 
  "IBM Plex Sans Arabic", "Vibes"
];

// Helper function to extract font name from CSS font-family value
const extractFontName = (fontValue: string): string => {
  if (!fontValue) return "";
  // Handle formats like "'Roboto', sans-serif" or "Roboto, sans-serif" or just "Roboto"
  const match = fontValue.match(/^['"]?([^'",]+)['"]?/);
  return match ? match[1].trim() : fontValue.trim();
};

// Helper function to format font value for CSS
const formatFontFamily = (fontName: string): string => {
  if (!fontName) return "";
  if (fontName.includes(",")) return fontName; // Already formatted
  return fontName.includes(" ") ? `"${fontName}", sans-serif` : `${fontName}, sans-serif`;
};

export const FontSelector: React.FC<FontSelectorProps> = ({
  label,
  options: providedOptions,
  value,
  onChange,
  placeholder = "Select a font...",
  previewText = "Aa أبج",
  showGroups = true,
  outputFormat = "name",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [googleFonts, setGoogleFonts] = useState<FontOption[]>([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load Google Fonts on mount
  useEffect(() => {
    const loadGoogleFonts = async () => {
      try {
        setLoading(true);
        // Comprehensive list of popular Google Fonts
        const popularGoogleFonts: FontOption[] = [
          { value: "Inter", label: "Inter", category: "google" },
          { value: "Roboto", label: "Roboto", category: "google" },
          { value: "Open Sans", label: "Open Sans", category: "google" },
          { value: "Lato", label: "Lato", category: "google" },
          { value: "Montserrat", label: "Montserrat", category: "google" },
          { value: "Oswald", label: "Oswald", category: "google" },
          { value: "Raleway", label: "Raleway", category: "google" },
          { value: "Poppins", label: "Poppins", category: "google" },
          { value: "Source Sans Pro", label: "Source Sans Pro", category: "google" },
          { value: "Playfair Display", label: "Playfair Display", category: "google" },
          { value: "Merriweather", label: "Merriweather", category: "google" },
          { value: "Ubuntu", label: "Ubuntu", category: "google" },
          { value: "Dancing Script", label: "Dancing Script", category: "google" },
          { value: "Pacifico", label: "Pacifico", category: "google" },
          { value: "Lobster", label: "Lobster", category: "google" },
          { value: "Bebas Neue", label: "Bebas Neue", category: "google" },
          { value: "Bangers", label: "Bangers", category: "google" },
          { value: "Righteous", label: "Righteous", category: "google" },
          { value: "Fredoka One", label: "Fredoka One", category: "google" },
          { value: "Comfortaa", label: "Comfortaa", category: "google" },
          { value: "Quicksand", label: "Quicksand", category: "google" },
          { value: "Caveat", label: "Caveat", category: "google" },
          { value: "Kalam", label: "Kalam", category: "google" },
          { value: "Permanent Marker", label: "Permanent Marker", category: "google" },
          { value: "Shadows Into Light", label: "Shadows Into Light", category: "google" },
          { value: "Indie Flower", label: "Indie Flower", category: "google" },
          { value: "Crimson Text", label: "Crimson Text", category: "google" },
          { value: "Fira Sans", label: "Fira Sans", category: "google" },
          { value: "PT Sans", label: "PT Sans", category: "google" },
          { value: "Nunito", label: "Nunito", category: "google" },
          { value: "Work Sans", label: "Work Sans", category: "google" },
          { value: "Rubik", label: "Rubik", category: "google" },
          { value: "Noto Sans", label: "Noto Sans", category: "google" },
          { value: "Mukta", label: "Mukta", category: "google" },
          { value: "DM Sans", label: "DM Sans", category: "google" },
          { value: "Manrope", label: "Manrope", category: "google" },
          { value: "Outfit", label: "Outfit", category: "google" },
          { value: "Plus Jakarta Sans", label: "Plus Jakarta Sans", category: "google" },
          { value: "Trirong", label: "Trirong", category: "google" },
          // Arabic Fonts
          { value: "Amiri", label: "Amiri", category: "google" },
          { value: "Changa", label: "Changa", category: "google" },
          { value: "El Messiri", label: "El Messiri", category: "google" },
          { value: "Harmattan", label: "Harmattan", category: "google" },
          { value: "Lateef", label: "Lateef", category: "google" },
          { value: "Markazi Text", label: "Markazi Text", category: "google" },
          { value: "Reem Kufi", label: "Reem Kufi", category: "google" },
          { value: "Scheherazade New", label: "Scheherazade New", category: "google" },
          { value: "Noto Sans Arabic", label: "Noto Sans Arabic", category: "google" },
          { value: "Noto Naskh Arabic", label: "Noto Naskh Arabic", category: "google" },
          { value: "Noto Kufi Arabic", label: "Noto Kufi Arabic", category: "google" },
          { value: "IBM Plex Sans Arabic", label: "IBM Plex Sans Arabic", category: "google" },
          { value: "Cairo", label: "Cairo", category: "google" },
          { value: "Tajawal", label: "Tajawal", category: "google" },
          { value: "Almarai", label: "Almarai", category: "google" },
        ];
        setGoogleFonts(popularGoogleFonts);
      } catch (error) {
        console.warn("Error loading Google Fonts:", error);
        // Continue with system fonts only
      } finally {
        setLoading(false);
      }
    };

    loadGoogleFonts();
  }, []);

  // Load font dynamically (Google Fonts and Typeface fonts)
  const loadFont = React.useCallback((fontName: string) => {
    if (!fontName) return;

    // We keep the full list for selection, but only load fonts on demand.
    // System fonts don't need loading. For others, best-effort load from Google Fonts.
    ensureFontLoaded(fontName).catch(() => {
      // Non-fatal; preview can fall back
    });
  }, []);

  // Load selected font dynamically
  useEffect(() => {
    const fontName = extractFontName(value || "");
    if (fontName) {
      loadFont(fontName);
    }
  }, [value, loadFont]);

  // Combine system fonts, Google Fonts, and Typeface fonts
  // If providedOptions is given, only show those options (for 3D designer - only supported fonts)
  // Otherwise, show all available fonts
  // Remove duplicates by font value (normalized)
  const allDefaultOptions = [...SYSTEM_FONTS, ...googleFonts, ...TYPEFACE_FONTS];
  const uniqueDefaultOptions = allDefaultOptions.filter((option, index, self) =>
    index === self.findIndex((opt) => opt.value.toLowerCase() === option.value.toLowerCase())
  );
  const allOptions = providedOptions 
    ? providedOptions // Only show provided options (supported fonts for 3D)
    : uniqueDefaultOptions;

  // Group fonts by category
  const groupedFonts = useMemo((): FontGroup[] => {
    if (!showGroups || providedOptions) {
      return [{ label: "Fonts", fonts: allOptions }];
    }

    // Separate Arabic fonts from other fonts
    const arabicFonts = allOptions.filter(f => 
      ARABIC_FONTS.some(af => af.toLowerCase() === f.value.toLowerCase()) ||
      f.value.toLowerCase().includes("arabic")
    );
    const nonArabicFonts = allOptions.filter(f => 
      !ARABIC_FONTS.some(af => af.toLowerCase() === f.value.toLowerCase()) &&
      !f.value.toLowerCase().includes("arabic")
    );

    const groups: FontGroup[] = [
      { label: "Arabic Fonts", fonts: arabicFonts },
      { label: "Google Fonts", fonts: nonArabicFonts.filter(f => f.category === "google" || googleFonts.some(gf => gf.value === f.value)) },
      { label: "Typeface Fonts", fonts: nonArabicFonts.filter(f => f.category === "typeface" || TYPEFACE_FONTS.some(tf => tf.value === f.value)) },
      { label: "System Fonts", fonts: nonArabicFonts.filter(f => f.category === "system" || SYSTEM_FONTS.some(sf => sf.value === f.value)) },
    ];

    return groups.filter(group => group.fonts.length > 0);
  }, [allOptions, showGroups, providedOptions, googleFonts]);

  // Find selected option - handle both formats (just name or full CSS)
  const selectedOption = useMemo(() => {
    if (!value) return null;
    const fontName = extractFontName(value);
    return allOptions.find((opt) => {
      const optFontName = extractFontName(opt.value);
      return optFontName.toLowerCase() === fontName.toLowerCase() ||
        opt.value.toLowerCase() === fontName.toLowerCase();
    });
  }, [value, allOptions]);

  // Filter fonts based on search query
  const filteredGroups = useMemo((): FontGroup[] => {
    if (!searchQuery) return groupedFonts;

    const query = searchQuery.toLowerCase();
    return groupedFonts.map(group => ({
      ...group,
      fonts: group.fonts.filter(opt => 
        opt.label.toLowerCase().includes(query) || 
        opt.value.toLowerCase().includes(query)
      ),
    })).filter(group => group.fonts.length > 0);
  }, [groupedFonts, searchQuery]);

  // Get font family CSS value for rendering
  const getFontFamilyCSS = (fontOption: FontOption): string => {
    const fontName = extractFontName(fontOption.value);
    return formatFontFamily(fontName);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleSelect = (option: FontOption) => {
    if (onChange) {
      // Return value in the requested format
      const outputValue = outputFormat === "css" 
        ? formatFontFamily(option.value)
        : option.value;
      onChange({ target: { value: outputValue } });
    }
    setIsOpen(false);
    setSearchQuery("");
  };

  if (loading) {
    return (
      <div className="w-full">
        {label && (
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {label}
          </label>
        )}
        <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 flex items-center justify-center">
          <span className="text-sm text-gray-500">Loading fonts...</span>
        </div>
      </div>
    );
  }

  const selectedFontFamily = selectedOption 
    ? getFontFamilyCSS(selectedOption)
    : "Arial, sans-serif";

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
        </label>
      )}
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="w-full px-3 py-2.5 text-left border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex items-center justify-between hover:border-gray-400 dark:hover:border-gray-500 transition-colors"
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <span
              className="text-lg font-medium text-gray-700 dark:text-gray-200"
              style={{ fontFamily: selectedFontFamily }}
              title={`Preview: ${previewText}`}
            >
              {previewText}
            </span>
            <span
              className="text-sm text-gray-600 dark:text-gray-400 truncate"
              style={{ fontFamily: selectedFontFamily }}
            >
              {selectedOption?.label || placeholder}
            </span>
          </div>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? "transform rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-xl max-h-96 overflow-hidden">
            <div className="p-2 border-b border-gray-200 dark:border-gray-700">
              <input
                type="text"
                placeholder="Search fonts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>
            <div className="overflow-y-auto max-h-80">
              {filteredGroups.length === 0 ? (
                <div className="px-4 py-8 text-sm text-gray-500 dark:text-gray-400 text-center">
                  No fonts found
                </div>
              ) : (
                filteredGroups.map((group) => (
                  <div key={group.label} className="py-1">
                    {showGroups && filteredGroups.length > 1 && (
                      <div className="px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-900 sticky top-0">
                        {group.label}
                      </div>
                    )}
                    {group.fonts.map((option) => {
                      const fontFamilyValue = getFontFamilyCSS(option);
                      const optionFontName = extractFontName(option.value);
                      const valueFontName = extractFontName(value || "");
                      const isSelected = selectedOption?.value === option.value || 
                        optionFontName.toLowerCase() === valueFontName.toLowerCase();
                      
                      const fontName = extractFontName(option.value);
                      
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => handleSelect(option)}
                          className={`w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center justify-between ${
                            isSelected ? "bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500" : ""
                          }`}
                          onMouseEnter={() => loadFont(fontName)}
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <span
                              className="text-xl font-medium text-gray-700 dark:text-gray-200 flex-shrink-0"
                              style={{ fontFamily: fontFamilyValue }}
                              title={`Preview: ${previewText}`}
                            >
                              {previewText}
                            </span>
                            <span
                              className="text-sm text-gray-600 dark:text-gray-400 truncate"
                              style={{ fontFamily: fontFamilyValue }}
                            >
                              {option.label}
                            </span>
                          </div>
                          {isSelected && (
                            <svg 
                              className="w-5 h-5 text-blue-500 flex-shrink-0 ml-2" 
                              fill="currentColor" 
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FontSelector;

